import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useUserData } from "features/User";
import * as collection from "@dicebear/collection";
import startCase from "lodash/startCase";
import AvatarPropertyField from "./AvatarPropertyField";
import AvatarPreview from "./AvatarPreview";

function AvatarPlayground() {
  const { avatarProps } = useSelector((state) => state.data);
  const { user } = useSelector((state) => state.auth);
  const { updateUserAvatar } = useUserData();

  const [seed, setSeed] = useState(user?.userId || "");
  const [avatarStyle, setAvatarStyle] = useState("identicon");
  const [schema, setSchema] = useState(null);
  const [customOptions, setCustomOptions] = useState({});
  const [initialized, setInitialized] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleCustomOptionChange = (key, value) => {
    setCustomOptions((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    const style = collection[avatarStyle];
    const styleSchema = style?.schema;

    if (avatarStyle !== "identicon" && styleSchema?.properties) {
      setSchema(styleSchema);

      const validKeys = Object.keys(styleSchema.properties);
      const newOptions = {};

      validKeys.forEach((key) => {
        const fieldSchema = styleSchema.properties[key];

        const enumOptions =
          fieldSchema.enum ||
          (fieldSchema.type === "array" && fieldSchema.items?.type === "string"
            ? fieldSchema.default || []
            : []);

        const isSliderField =
          fieldSchema.type === "integer" &&
          typeof fieldSchema.default === "number" &&
          typeof fieldSchema.minimum === "number" &&
          typeof fieldSchema.maximum === "number";

        if (
          !initialized &&
          avatarProps?.avatarSchema === avatarStyle &&
          avatarProps.avatarConfig?.[key] !== undefined
        ) {
          newOptions[key] = avatarProps.avatarConfig[key];
        } else if (enumOptions.length > 0) {
          newOptions[key] = enumOptions[0];
        } else if (isSliderField) {
          newOptions[key] = key.toLowerCase().endsWith("probability")
            ? fieldSchema.maximum
            : fieldSchema.default;
        } else if (fieldSchema.default !== undefined) {
          newOptions[key] = fieldSchema.default;
        }
      });

      setCustomOptions(newOptions);
      setInitialized(true);
    } else {
      setSchema(null);
      setCustomOptions({});
    }
  }, [avatarStyle]);

  // Pre-populate style/config from redux
  useEffect(() => {
    if (avatarProps?.avatarSchema) {
      setAvatarStyle(avatarProps.avatarSchema);
      setSeed(user?.userId || "");
    } else {
      setAvatarStyle("identicon");
      setCustomOptions({});
      setSeed(user?.userId || "");
    }
  }, [avatarProps]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await updateUserAvatar({
        userAvatar: {
          avatarSchema: avatarStyle,
          avatarConfig: customOptions,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: { sm: "100%", md: "60%" } }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "300px 1fr",
          gap: 2,
          alignItems: "start",
          mb: 4,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <FormControl variant="outlined" sx={{ width: 300 }}>
            <InputLabel id="avatar-style-label" htmlFor="avatar-style">
              Avatar Style
            </InputLabel>
            <Select
              label="Avatar Style"
              labelId="avatar-style-label"
              value={avatarStyle}
              onChange={(e) => {
                setAvatarStyle(e.target.value);
                setInitialized(false);
              }}
              slotProps={{
                input: {
                  id: "avatar-style",
                },
              }}
            >
              {Object.keys(collection).map((styleKey) => (
                <MenuItem key={styleKey} value={styleKey}>
                  {startCase(styleKey)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {schema?.properties &&
            Object.entries(schema.properties).map(([key, fieldSchema]) => (
              <AvatarPropertyField
                key={key}
                propertyKey={key}
                fieldSchema={fieldSchema}
                avatarStyle={avatarStyle}
                customOptions={customOptions}
                onChange={handleCustomOptionChange}
                seed={seed}
              />
            ))}
        </Box>
        <AvatarPreview
          avatarStyle={avatarStyle}
          customOptions={customOptions}
          seed={seed}
        />
      </Box>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: "flex", justifyContent: "end" }}>
        <Button
          onClick={handleSave}
          disabled={loading}
          sx={{ display: "flex", gap: 1 }}
        >
          {loading && <CircularProgress size={20} />}
          Save
        </Button>
      </Box>
    </Box>
  );
}

export default AvatarPlayground;
